import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { IMAGES } from "@/lib/site";
import { TiltCard } from "@/components/site/TiltCard";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const badgeY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 80]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative pt-32 md:pt-40 pb-20 md:pb-28 px-6 md:px-12 lg:px-16 overflow-hidden">
      <div className="mx-auto max-w-[1400px] grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          style={{ y: textY }}
          className="md:col-span-7"
        >
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#0047AB] mb-6">
            Software & Cloud Consulting
          </p>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] text-[#0A0A0A]">
            Precision Cloud
            <br />
            Strategy &amp;
            <br />
            <span className="text-[#0047AB]">Technical Clarity.</span>
          </h1>
          <p className="mt-8 max-w-xl text-lg leading-relaxed text-[#52525B]">
            Rae Cloud LLC helps teams architect resilient systems and translate
            complex engineering into documentation people actually understand.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a
              href="#contact"
              data-testid="hero-cta-button"
              className="group inline-flex items-center justify-center gap-2 bg-[#0047AB] hover:bg-[#003380] text-white font-medium px-8 py-4 rounded-none transition-colors"
            >
              Start a Conversation
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="#services"
              data-testid="hero-secondary-button"
              className="inline-flex items-center justify-center bg-transparent border border-black text-black hover:bg-black hover:text-white font-medium px-8 py-4 rounded-none transition-colors"
            >
              View Services
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.15 }}
          className="md:col-span-5 relative"
        >
          <TiltCard max={7} className="relative">
            <div className="relative overflow-hidden border border-gray-200 shadow-2xl shadow-gray-400/30">
              <motion.img
                src={IMAGES.hero}
                alt="Modern glass architecture"
                style={{ y: imgY, scale: 1.15 }}
                className="w-full h-[380px] md:h-[520px] object-cover grayscale-[30%]"
              />
              <div className="absolute inset-0 bg-[#0047AB] mix-blend-multiply opacity-15" />
            </div>
            <motion.div
              style={{ y: badgeY, transform: "translateZ(60px)" }}
              className="absolute -bottom-5 -left-5 bg-[#0A0A0A] text-white px-6 py-4 hidden md:block shadow-xl"
            >
              <p className="font-mono text-xs tracking-widest uppercase text-gray-400">Uptime focus</p>
              <p className="font-heading text-2xl font-bold">99.9% reliability</p>
            </motion.div>
          </TiltCard>
        </motion.div>
      </div>
    </section>
  );
};
